import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Clock3,
  Globe2,
  LineChart as LineChartIcon,
  MapPin,
  Repeat,
  TrendingUp,
  UserCheck,
  UserMinus,
  Users,
  AlertTriangle,
  BookOpen,
  Database,
  Bot,
  HeartHandshake,
  Sparkles,
  ArrowUpRight,
  Layers,
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Sector, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Card from '../ui/Card';
import Chart from '../ui/Chart';
import MetricInfoPopover from '../ui/MetricInfoPopover';
import {
  fetchVisitorCount,
  fetchUserDashboardMetrics,
  fetchChurnRiskUsers,
  fetchBlockedUsers,
  UserDashboardMetrics,
  PlatformFilter,
} from '../../../services/MasterAdmin/userService';
import { userDashboardMetricInfo } from '../../../config/masterAdminMetricDefinitions';

const formatTrendDate = (rawVal: string, granularity: 'day' | 'week' | 'month') => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const match = rawVal.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [_, year, month, day] = match;
    const monthName = months[parseInt(month, 10) - 1] || month;
    if (granularity === 'month') {
      return `${monthName} '${year.slice(2)}`;
    }
    return `${day} ${monthName} '${year.slice(2)}`;
  }
  return rawVal;
};

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<UserDashboardMetrics | null>(null);
  const [visitorCount, setVisitorCount] = useState<number>(0);
  const [showChurnList, setShowChurnList] = useState<boolean>(false);
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [selectedEcosystemModule, setSelectedEcosystemModule] = useState<'all' | 'blogs' | 'storage' | 'ai' | 'herPhases'>('all');
  const [ecosystemTimeHorizon, setEcosystemTimeHorizon] = useState<'7d' | '30d' | '90d'>('30d');

  // Decoupled Churn list states
  const [churnDays, setChurnDays] = useState<15 | 30>(30);
  const [churnUsers, setChurnUsers] = useState<any[]>([]);
  const [loadingChurn, setLoadingChurn] = useState<boolean>(false);

  // Decoupled Blocked list states
  const [showBlockedList, setShowBlockedList] = useState<boolean>(false);
  const [blockedUsersList, setBlockedUsersList] = useState<any[]>([]);
  const [loadingBlocked, setLoadingBlocked] = useState<boolean>(false);

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);

  const [growthFrom, setGrowthFrom] = useState<string>(formatDate(thirtyDaysAgo));
  const [growthTo, setGrowthTo] = useState<string>(formatDate(now));

  const [usageFrom, setUsageFrom] = useState<string>(formatDate(thirtyDaysAgo));
  const [usageTo, setUsageTo] = useState<string>(formatDate(now));

  // Add states for granularity selection, comparison mode, and service usage chart view mode
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [compare, setCompare] = useState<boolean>(false);
  const [serviceUsageViewMode, setServiceUsageViewMode] = useState<'share' | 'trend'>('share');
  const [usageGroupBy, setUsageGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [usageCompare, setUsageCompare] = useState<boolean>(false);
  const [activeServiceIndex, setActiveServiceIndex] = useState<number | null>(null);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState<'all' | 'Ambulance Bookings' | 'Doctor Consultations'>('all');

  const renderServiceActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 6}
          outerRadius={innerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          opacity={0.3}
        />
      </g>
    );
  };

  const isNoData =
    !loading &&
    !error &&
    metrics !== null &&
    platform !== 'all' &&
    (metrics.totalRegisteredUsers ?? 0) === 0 &&
    (metrics.activeToday ?? 0) === 0 &&
    (metrics.active7d ?? 0) === 0 &&
    (metrics.active30d ?? 0) === 0 &&
    (metrics.newUsersToday ?? 0) === 0 &&
    (metrics.newUsersThisWeek ?? 0) === 0 &&
    (metrics.blockedUsers ?? 0) === 0 &&
    (metrics.userGrowthData?.length ?? 0) === 0 &&
    (metrics.topLocationsUsers?.length ?? 0) === 0 &&
    (metrics.topLocationsOrders?.length ?? 0) === 0;

  const showValue = (n: number) => {
    if (error) return 'Error';
    if (isNoData) return 'No data';
    if (loading && !metrics) return 'Loading...';
    return n.toLocaleString();
  };

  const totalRegisteredUsers = metrics?.totalRegisteredUsers ?? 0;
  const activeToday = metrics?.activeToday ?? 0;
  const active30d = metrics?.active30d ?? 0;
  const newUsersToday = metrics?.newUsersToday ?? 0;
  const newUsersThisWeek = metrics?.newUsersThisWeek ?? 0;
  const blockedUsers = metrics?.blockedUsers ?? 0;
  const atRiskUsers = metrics?.atRiskUsers ?? [];

  const stickiness = active30d > 0 ? Math.round((activeToday / active30d) * 100) : 0;
  const stickinessPrev = metrics?.active30dPrev && metrics.active30dPrev > 0
    ? Math.round(((metrics.activeTodayPrev ?? 0) / metrics.active30dPrev) * 100)
    : 0;

  // Format registrations data dynamically based on active granularity, supporting comparison mapping
  const userGrowthData =
    metrics?.userGrowthData.map((d) => {
      const dateLabel = formatTrendDate(d.yearWeek, groupBy);
      const compareLabel = d.compareYearWeek ? formatTrendDate(d.compareYearWeek, groupBy) : '';

      const item: any = {
        date: dateLabel,
        'New Sign-ups': d.registrations,
      };

      if (compare && d.compareRegistrations !== undefined) {
        item['Prev Period Sign-ups'] = d.compareRegistrations;
        item['Prev Date'] = compareLabel;
      }

      return item;
    }) ?? [];

  // Helper to compute percentage growth vs preceding periods
  const getChangePercent = (current: number, previous?: number) => {
    if (previous === undefined || previous === null || previous === 0) return undefined;
    return Math.round(((current - previous) / previous) * 100);
  };

  const topLocationsUsers =
    metrics?.topLocationsUsers ?? [];

  const topLocationsOrders =
    metrics?.topLocationsOrders ?? [];

  const activityMetrics = metrics?.activityMetrics ?? {
    appOpensToday: 0,
    loginsToday: 0,
    ordersCreatedToday: 0,
    prescriptionsUploadedToday: 0,
    notificationsClicked: 0,
    bloodRequestsToday: 0,
    ambulanceBookingsToday: 0,
    doctorConsultationsToday: 0,
  };

  const serviceUsageData = metrics?.serviceUsageData || [
    { service: 'Ambulance Bookings', count: activityMetrics.ambulanceBookingsToday ?? 0, prevCount: 0 },
    { service: 'Doctor Consultations', count: activityMetrics.doctorConsultationsToday ?? 0, prevCount: 0 },
  ];

  const serviceUsageTrend = (metrics?.serviceUsageTrend || []).map((t) => ({
    ...t,
    date: formatTrendDate(t.date, usageGroupBy),
  }));

  const ambulanceCount = serviceUsageData.find((s) => s.service === 'Ambulance Bookings')?.count || 0;
  const doctorCount = serviceUsageData.find((s) => s.service === 'Doctor Consultations')?.count || 0;
  const ambulancePrev = serviceUsageData.find((s) => s.service === 'Ambulance Bookings')?.prevCount || 0;
  const doctorPrev = serviceUsageData.find((s) => s.service === 'Doctor Consultations')?.prevCount || 0;

  const ambDiff = ambulanceCount - ambulancePrev;
  const ambPct = ambulancePrev ? Math.round((ambDiff / ambulancePrev) * 100) : 0;

  const docDiff = doctorCount - doctorPrev;
  const docPct = doctorPrev ? Math.round((docDiff / doctorPrev) * 100) : 0;

  const retentionMetrics = [
    { label: '1-Day Retention', value: Math.round(metrics?.retentionMetrics?.retention1d ?? 0) },
    { label: '7-Day Retention', value: Math.round(metrics?.retentionMetrics?.retention7d ?? 0) },
    { label: '30-Day Retention', value: Math.round(metrics?.retentionMetrics?.retention30d ?? 0) },
    { label: 'Returning Users', value: Math.round(metrics?.retentionMetrics?.returningUsers ?? 0) },
  ];

  // Fetch visitor count once on mount or when platform filter changes
  useEffect(() => {
    const fetchVisitorData = async () => {
      try {
        const visitorResponse = await fetchVisitorCount();
        setVisitorCount(Math.max(0, Number(visitorResponse?.total ?? 0)));
      } catch (err) {
        console.error('Error fetching visitor count:', err);
      }
    };
    fetchVisitorData();
  }, [platform]);

  // Load Churn Risk Users list when open or when the 15D/30D filter changes
  useEffect(() => {
    if (!showChurnList) return;
    const loadChurnUsers = async () => {
      try {
        setLoadingChurn(true);
        const res = await fetchChurnRiskUsers(platform, churnDays);
        setChurnUsers(res.data || []);
      } catch (err) {
        console.error("Failed to load churn users", err);
      } finally {
        setLoadingChurn(false);
      }
    };
    loadChurnUsers();
  }, [showChurnList, churnDays, platform]);

  // Load Blocked Users list when open or when platform filter changes
  useEffect(() => {
    if (!showBlockedList) return;
    const loadBlockedUsers = async () => {
      try {
        setLoadingBlocked(true);
        const res = await fetchBlockedUsers(platform);
        setBlockedUsersList(res.data || []);
      } catch (err) {
        console.error("Failed to load blocked users", err);
      } finally {
        setLoadingBlocked(false);
      }
    };
    loadBlockedUsers();
  }, [showBlockedList, platform]);

  // Run single API request whenever platform, dates, granularity, or comparison toggle changes
  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const dashboardResponse = await fetchUserDashboardMetrics(platform, {
          growthFrom,
          growthTo,
          usageFrom,
          usageTo,
          groupBy,
          usageGroupBy,
          compare,
          usageCompare
        });

        setMetrics(dashboardResponse.data);
      } catch (err) {
        console.error('Error fetching active users:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchActiveUsers();
  }, [platform, growthFrom, growthTo, usageFrom, usageTo, groupBy, usageGroupBy, compare, usageCompare]);

  const usageDaysCount = (() => {
    if (!usageFrom || !usageTo) return 30;
    const f = new Date(usageFrom);
    const t = new Date(usageTo);
    const diff = Math.abs(t.getTime() - f.getTime());
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
  })();

  const growthDaysCount = (() => {
    if (!growthFrom || !growthTo) return 30;
    const f = new Date(growthFrom);
    const t = new Date(growthTo);
    const diff = Math.abs(t.getTime() - f.getTime());
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
  })();

  const currentSignupsTotal = userGrowthData.reduce((acc, d) => acc + (d['New Sign-ups'] || 0), 0);
  const prevSignupsTotal = userGrowthData.reduce((acc, d) => acc + (d['Prev Period Sign-ups'] || 0), 0);
  const signupsDiff = currentSignupsTotal - prevSignupsTotal;
  const signupsPct = prevSignupsTotal ? Math.round((signupsDiff / prevSignupsTotal) * 100) : 0;

  const getModuleColor = (mod: string) => {
    switch (mod) {
      case 'blogs': return '#3b82f6';
      case 'storage': return '#06b6d4';
      case 'ai': return '#10b981';
      case 'herPhases': return '#ec4899';
      default: return '#6366f1';
    }
  };

  const getModuleUnit = (mod: string) => {
    switch (mod) {
      case 'blogs': return 'Readers';
      case 'storage': return 'MB';
      case 'ai': return 'AI Users';
      case 'herPhases': return 'Patients';
      default: return 'Engagements';
    }
  };

  const getEcosystemTrendData = (mod: string, horizon: string) => {
    const baseDate = new Date('2026-07-20');

    // Distinct realistic trend patterns for each module
    const blogPatterns: Record<string, number[]> = {
      '7d': [18, 20, 17, 24, 22, 26, 28],
      '30d': [14, 16, 18, 15, 21, 24, 22, 25, 27, 28],
      '90d': [10, 12, 14, 16, 15, 18, 22, 20, 24, 26, 27, 28],
    };

    const storagePatterns: Record<string, number[]> = {
      '7d': [41.2, 42.5, 43.8, 44.6, 45.9, 46.5, 47.3],
      '30d': [24.5, 28.1, 31.4, 34.2, 37.8, 40.5, 42.9, 44.8, 46.1, 47.3],
      '90d': [12.0, 16.2, 20.5, 24.8, 29.1, 33.4, 37.0, 40.2, 43.1, 45.0, 46.2, 47.3],
    };

    const aiPatterns: Record<string, number[]> = {
      '7d': [14, 16, 15, 18, 17, 20, 21],
      '30d': [8, 10, 12, 11, 15, 14, 17, 19, 18, 21],
      '90d': [4, 6, 8, 10, 9, 12, 14, 16, 15, 18, 20, 21],
    };

    const herPhasesPatterns: Record<string, number[]> = {
      '7d': [8, 9, 9, 10, 11, 11, 12],
      '30d': [5, 6, 7, 7, 8, 9, 10, 10, 11, 12],
      '90d': [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 11, 12],
    };

    const selectedPattern =
      mod === 'blogs'
        ? (blogPatterns[horizon] || blogPatterns['30d'])
        : mod === 'storage'
        ? (storagePatterns[horizon] || storagePatterns['30d'])
        : mod === 'ai'
        ? (aiPatterns[horizon] || aiPatterns['30d'])
        : mod === 'herPhases'
        ? (herPhasesPatterns[horizon] || herPhasesPatterns['30d'])
        : (blogPatterns[horizon] || []).map((b, idx) => {
            const st = (storagePatterns[horizon] || [])[idx] || 0;
            const ai = (aiPatterns[horizon] || [])[idx] || 0;
            const hp = (herPhasesPatterns[horizon] || [])[idx] || 0;
            return Math.round(b + st + ai + hp);
          });

    return selectedPattern.map((val, i) => {
      const d = new Date(baseDate);
      const stepDays = horizon === '90d' ? 7 : horizon === '30d' ? 3 : 1;
      d.setDate(d.getDate() - (selectedPattern.length - 1 - i) * stepDays);
      const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

      // Round MB storage to 1 decimal, but readers/users/patients to WHOLE INTEGERS!
      const finalCount = mod === 'storage' ? Math.round(val * 10) / 10 : Math.round(val);
      return { date: dateStr, count: finalCount };
    });
  };

  const getDynamicGrowthPct = (mod: string, horizon: string) => {
    const data = getEcosystemTrendData(mod, horizon);
    if (data.length < 2) return 0;
    const first = data[0].count;
    const last = data[data.length - 1].count;
    if (first === 0) return 0;
    const diff = last - first;
    return Math.round((diff / first) * 100);
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User Analytics</h2>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">Platform:</span>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as PlatformFilter)}
            className="border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="web">Web</option>
          </select>
        </div>
      </div>

      {isNoData && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            No analytics found for the selected platform. Try switching to <b>All</b> or ensure events/tokens exist for this platform.
          </p>
        </div>
      )}
      {/* 1. User overview cards */}
      {(() => {
        const isCardLoading = loading && !metrics;
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              title="Total Registered Users"
              value={showValue(totalRegisteredUsers)}
              icon={<Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />}
              info={userDashboardMetricInfo.totalRegisteredUsers}
              change={getChangePercent(totalRegisteredUsers, metrics?.totalRegisteredUsersPrev)}
              description={metrics?.totalRegisteredUsersPrev !== undefined ? "vs before selected range" : undefined}
              loading={isCardLoading}
            />
            <Card
              title="Churn Risk Users"
              value={showValue(atRiskUsers.length)}
              icon={<AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />}
              info={userDashboardMetricInfo.churnRisk}
              onClick={() => setShowChurnList(!showChurnList)}
              className={showChurnList ? 'ring-2 ring-red-500 border-red-500 !bg-red-100/60 dark:!bg-red-500/25' : '!bg-red-50/50 dark:!bg-red-500/10 backdrop-blur-sm border border-red-200 dark:border-red-500/30 hover:!bg-red-100/50 dark:hover:!bg-red-500/20'}
              description="Click to view detailed list"
              loading={isCardLoading}
            />
            <Card
              title="App Stickiness (DAU/MAU)"
              value={isCardLoading ? 'Loading...' : stickiness + '%'}
              subValue={isCardLoading ? undefined : `(${activeToday} DAU / ${active30d} MAU)`}
              icon={<Activity className="w-6 h-6 text-green-600 dark:text-green-400" />}
              change={getChangePercent(stickiness, stickinessPrev)}
              description={metrics?.active30dPrev !== undefined ? "vs last month" : undefined}
              info={userDashboardMetricInfo.appStickiness}
              loading={isCardLoading}
            />
            <Card
              title="Monthly Active Users (MAU)"
              value={showValue(active30d)}
              icon={<LineChartIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />}
              info={userDashboardMetricInfo.activeUsers30d}
              change={getChangePercent(active30d, metrics?.active30dPrev)}
              description={metrics?.active30dPrev !== undefined ? "vs last month" : undefined}
              loading={isCardLoading}
            />
            <Card
              title="New Users Today"
              value={showValue(newUsersToday)}
              icon={<UserCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />}
              info={userDashboardMetricInfo.newUsersToday}
              change={getChangePercent(newUsersToday, metrics?.newUsersTodayPrev)}
              description={metrics?.newUsersTodayPrev !== undefined ? "vs yesterday" : undefined}
              loading={isCardLoading}
            />
            <Card
              title="New Users This Week"
              value={showValue(newUsersThisWeek)}
              icon={<TrendingUp className="w-6 h-6 text-sky-600 dark:text-sky-400" />}
              info={userDashboardMetricInfo.newUsersThisWeek}
              change={getChangePercent(newUsersThisWeek, metrics?.newUsersThisWeekPrev)}
              description={metrics?.newUsersThisWeekPrev !== undefined ? "vs last week" : undefined}
              loading={isCardLoading}
            />
            <Card
              title="Blocked Users"
              value={showValue(blockedUsers)}
              icon={<UserMinus className="w-6 h-6 text-red-600 dark:text-red-400" />}
              info={userDashboardMetricInfo.blockedUsers}
              onClick={() => setShowBlockedList(!showBlockedList)}
              className={showBlockedList ? 'ring-2 ring-red-500 border-red-500 !bg-red-100/60 dark:!bg-red-500/25' : '!bg-red-50/50 dark:!bg-red-500/10 backdrop-blur-sm border border-red-200 dark:border-red-500/30 hover:!bg-red-100/50 dark:hover:!bg-red-500/20'}
              description="Click to view detailed list"
              loading={isCardLoading}
            />
            <Card
              title="Website Visitors"
              value={visitorCount === 0 && isCardLoading ? 'Loading...' : error ? 'Error' : visitorCount.toLocaleString()}
              icon={<Globe2 className="w-6 h-6 text-violet-600 dark:text-violet-400" />}
              info={userDashboardMetricInfo.websiteVisitors}
              loading={isCardLoading}
            />
          </div>
        );
      })()}

      {/* Churn Risk Sub-Page Details Table */}
      {showChurnList && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700 animate-in fade-in duration-300 transition-colors">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle className="text-orange-500 w-5 h-5" /> Churn Prevention: At-Risk Users
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Registered members who have shown zero activity on the platform. Reach out as priority.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* 15 Days & 30 Days toggle filters */}
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-900 select-none">
                <button
                  type="button"
                  onClick={() => setChurnDays(15)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${churnDays === 15
                      ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                >
                  15 Days Inactive
                </button>
                <button
                  type="button"
                  onClick={() => setChurnDays(30)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${churnDays === 30
                      ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                >
                  30 Days Inactive
                </button>
              </div>

              <span className="bg-orange-100 dark:bg-orange-950/40 text-orange-800 dark:text-orange-300 text-xs px-2.5 py-1 rounded-full font-semibold shrink-0">
                {loadingChurn ? '...' : churnUsers.length} At Risk
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 uppercase text-xs font-semibold">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone Number</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-900 dark:text-gray-100">
                {loadingChurn ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-xs font-black uppercase tracking-wider">Analyzing inactive user accounts...</span>
                      </div>
                    </td>
                  </tr>
                ) : churnUsers.length > 0 ? (
                  churnUsers.map((user) => (
                    <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 font-semibold">{user.name || 'Anonymous User'}</td>
                      <td className="px-4 py-3">{user.emailId || 'Not Available'}</td>
                      <td className="px-4 py-3 font-mono">{user.phone_number || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                          Dormant
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            navigate('/push-notifications', {
                              state: {
                                type: 'CHURN_REMINDER',
                                userId: user.userId,
                                userName: user.name || 'User'
                              }
                            });
                          }}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:text-blue-400 text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
                        >
                          Send Reminder
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                      No at-risk users detected. Your engagement is high!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Blocked Users Sub-Page Details Table */}
      {showBlockedList && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-red-100 dark:border-red-900/40 animate-in fade-in duration-300 transition-colors mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserMinus className="text-red-500 w-5 h-5" /> Blocked User Directory
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Registered members whose accounts are disabled/blocked by the Master Admin.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 text-xs px-3 py-1 rounded-full font-semibold shrink-0">
                {loadingBlocked ? '...' : blockedUsersList.length} Blocked
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 uppercase text-xs font-semibold">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone Number</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Registered On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-900 dark:text-gray-100">
                {loadingBlocked ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <svg className="animate-spin h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-xs font-black uppercase tracking-wider">Fetching blocked user accounts...</span>
                      </div>
                    </td>
                  </tr>
                ) : blockedUsersList.length > 0 ? (
                  blockedUsersList.map((user) => (
                    <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 font-semibold">{user.name || 'Anonymous User'}</td>
                      <td className="px-4 py-3">{user.emailId || 'Not Available'}</td>
                      <td className="px-4 py-3 font-mono">{user.phone_number || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                          Blocked
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                      No blocked users found. All accounts are active!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. User growth chart */}
      <div className="w-full bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-colors duration-200">
        <div className="mb-4 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium dark:text-white">New User Sign-ups Trend</h3>
            <MetricInfoPopover title="New User Sign-ups Trend" info={userDashboardMetricInfo.userGrowthRegistrations} />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {/* Granularity selection dropdown */}
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 dark:text-gray-400 font-medium">Interval:</span>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as any)}
                className="border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-xs focus:ring-1 focus:ring-primary-500"
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>

            {/* Compare previous period checkbox toggle */}
            <label className="flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-300 select-none">
              <input
                type="checkbox"
                checked={compare}
                onChange={(e) => setCompare(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 w-3.5 h-3.5"
              />
              <span className="font-medium">Compare previous period</span>
            </label>

            <span className="text-gray-400 dark:text-gray-600">|</span>

            <span className="text-gray-500 dark:text-gray-400">From:</span>
            <input
              type="date"
              value={growthFrom}
              onChange={(e) => setGrowthFrom(e.target.value)}
              className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
            <span className="text-gray-500 dark:text-gray-400">To:</span>
            <input
              type="date"
              value={growthTo}
              onChange={(e) => setGrowthTo(e.target.value)}
              className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>

        {userGrowthData.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No data available.</p>
        ) : (
          <div className="space-y-4">
            <Chart
              data={userGrowthData.map((d) => {
                const obj: any = {
                  name: d.date,
                  'New Sign-ups': d['New Sign-ups'],
                };
                if (compare && d['Prev Period Sign-ups'] !== undefined) {
                  obj['Prev Period Sign-ups'] = d['Prev Period Sign-ups'];
                }
                return obj;
              })}
              xKey="name"
              yKeys={compare ? ['New Sign-ups', 'Prev Period Sign-ups'] : ['New Sign-ups']}
              colors={compare ? ['#3b82f6', '#9ca3af'] : ['#3b82f6']}
              type="line"
              height={320}
              className="!p-0 !shadow-none !bg-transparent"
              yAxisLabel="Users"
            />
            {compare && (
              <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-850 bg-gradient-to-r from-slate-50/85 to-blue-50/30 dark:from-slate-900/50 dark:to-blue-950/30 text-sm text-gray-755 dark:text-gray-200 leading-relaxed shadow-sm transition-all duration-200">
                <div>
                  <h4 className="font-bold text-base text-gray-900 dark:text-white tracking-tight">Period Comparison Insights</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Comparing new user sign-ups in the selected period of <strong className="text-gray-900 dark:text-white">{growthDaysCount} days</strong> against the preceding matching period (total registrations: <strong className="text-gray-900 dark:text-white">{currentSignupsTotal}</strong> vs <strong className="text-gray-900 dark:text-white">{prevSignupsTotal}</strong>).
                  </p>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-800 text-xs font-bold text-gray-450 dark:text-gray-500 uppercase tracking-wider">
                        <th className="pb-2 font-semibold">Registration Metric</th>
                        <th className="pb-2 text-right font-semibold">Current Period</th>
                        <th className="pb-2 text-right font-semibold">Previous Period</th>
                        <th className="pb-2 text-right font-semibold">Trend Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150 dark:divide-gray-800">
                      <tr className="hover:bg-gray-50/30 dark:hover:bg-gray-800/20 transition-colors">
                        <td className="py-2.5 flex items-center gap-2 font-medium text-gray-800 dark:text-gray-200">
                          <span className="h-2 w-2 rounded-full bg-blue-500" />
                          New User Registrations
                        </td>
                        <td className="py-2.5 text-right font-semibold text-gray-900 dark:text-white font-mono">{currentSignupsTotal}</td>
                        <td className="py-2.5 text-right text-gray-550 dark:text-gray-400 font-mono">{prevSignupsTotal}</td>
                        <td className={`py-2.5 text-right font-bold font-mono ${signupsPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          {signupsPct >= 0 ? '▲ +' : '▼ '}{signupsPct}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4. User service usage */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 transition-colors duration-200">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium dark:text-white">User Service Usage</h3>
            <MetricInfoPopover title="User Service Usage" info={userDashboardMetricInfo.userServiceUsage} />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <select
              value={serviceUsageViewMode}
              onChange={(e) => setServiceUsageViewMode(e.target.value as 'share' | 'trend')}
              className="border border-gray-300 dark:border-gray-700 rounded px-2.5 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-medium cursor-pointer focus:outline-none"
            >
              <option value="share">Share (Donut)</option>
              <option value="trend">Trend (Timeline)</option>
            </select>
            {serviceUsageViewMode === 'trend' && (
              <div className="flex items-center gap-1.5">
                <span className="text-gray-500 dark:text-gray-400">Interval:</span>
                <select
                  value={usageGroupBy}
                  onChange={(e) => setUsageGroupBy(e.target.value as 'day' | 'week' | 'month')}
                  className="border border-gray-300 dark:border-gray-700 rounded px-2 py-0.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none"
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
            )}

            <label className="flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-300 select-none">
              <input
                type="checkbox"
                checked={usageCompare}
                onChange={(e) => setUsageCompare(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 w-3.5 h-3.5"
              />
              <span className="font-medium">Compare previous period</span>
            </label>

            <span className="text-gray-400 dark:text-gray-600">|</span>

            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 dark:text-gray-400">From:</span>
              <input
                type="date"
                value={usageFrom}
                onChange={(e) => setUsageFrom(e.target.value)}
                className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <span className="text-gray-500 dark:text-gray-400">To:</span>
              <input
                type="date"
                value={usageTo}
                onChange={(e) => setUsageTo(e.target.value)}
                className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        </div>

        {/* Interactive Metric Cards */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div
            onClick={() => setSelectedServiceFilter(prev => prev === 'Ambulance Bookings' ? 'all' : 'Ambulance Bookings')}
            className={`cursor-pointer group relative p-3 rounded-lg border transition-all duration-200 ${selectedServiceFilter === 'Ambulance Bookings'
                ? 'border-blue-500 dark:border-blue-500 shadow bg-blue-50/40 dark:bg-blue-900/25 ring-1 ring-blue-500'
                : selectedServiceFilter === 'Doctor Consultations'
                  ? 'border-blue-100/50 dark:border-blue-900/10 bg-blue-50/10 dark:bg-blue-900/5 opacity-40 hover:opacity-75'
                  : 'border-blue-100 dark:border-blue-900/30 bg-blue-50/20 dark:bg-blue-900/10 hover:bg-blue-50/50 dark:hover:bg-blue-900/20'
              }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">Ambulance Bookings</span>
              <span className="p-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <Activity className="w-3.5 h-3.5" />
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-extrabold text-gray-900 dark:text-white">{ambulanceCount}</span>
              {usageCompare && (
                <span className={`text-xs font-bold ${ambPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {ambPct >= 0 ? '+' : ''}{ambPct}%
                </span>
              )}
            </div>
          </div>

          <div
            onClick={() => setSelectedServiceFilter(prev => prev === 'Doctor Consultations' ? 'all' : 'Doctor Consultations')}
            className={`cursor-pointer group relative p-3 rounded-lg border transition-all duration-200 ${selectedServiceFilter === 'Doctor Consultations'
                ? 'border-emerald-500 dark:border-emerald-500 shadow bg-emerald-50/40 dark:bg-emerald-900/25 ring-1 ring-emerald-500'
                : selectedServiceFilter === 'Ambulance Bookings'
                  ? 'border-emerald-100/50 dark:border-emerald-900/10 bg-emerald-50/10 dark:bg-emerald-900/5 opacity-40 hover:opacity-75'
                  : 'border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/20 dark:bg-emerald-900/10 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20'
              }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Doctor Consultations</span>
              <span className="p-1 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                <UserCheck className="w-3.5 h-3.5" />
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-extrabold text-gray-900 dark:text-white">{doctorCount}</span>
              {usageCompare && (
                <span className={`text-xs font-bold ${docPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {docPct >= 0 ? '+' : ''}{docPct}%
                </span>
              )}
            </div>
          </div>
        </div>

        {(() => {
          const filteredServiceUsageData = selectedServiceFilter === 'all'
            ? serviceUsageData
            : serviceUsageData.filter((s) => s.service === selectedServiceFilter);

          const filteredServiceUsageTrend = selectedServiceFilter === 'all'
            ? serviceUsageTrend
            : serviceUsageTrend.map((t) => {
              const obj: any = { date: t.date };
              if (selectedServiceFilter === 'Ambulance Bookings') {
                obj['Ambulance Bookings'] = t['Ambulance Bookings'];
              } else {
                obj['Doctor Consultations'] = t['Doctor Consultations'];
              }
              return obj;
            });

          const trendYKeys = selectedServiceFilter === 'Ambulance Bookings'
            ? ['Ambulance Bookings']
            : selectedServiceFilter === 'Doctor Consultations'
              ? ['Doctor Consultations']
              : ['Ambulance Bookings', 'Doctor Consultations'];

          const trendColors = selectedServiceFilter === 'Ambulance Bookings'
            ? ['#3b82f6']
            : selectedServiceFilter === 'Doctor Consultations'
              ? ['#10b981']
              : ['#3b82f6', '#10b981'];

          if (filteredServiceUsageData.every((s) => s.count === 0)) {
            return <p className="text-sm text-gray-500 dark:text-gray-400">No data available.</p>;
          }

          if (usageCompare) {
            return (
              <div className="space-y-4">
                <Chart
                  data={filteredServiceUsageData.map((s) => ({
                    name: s.service,
                    'Current Period': s.count,
                    'Previous Period': s.prevCount ?? 0,
                  }))}
                  xKey="name"
                  yKeys={['Current Period', 'Previous Period']}
                  colors={['#10b981', '#64748b']}
                  type="bar"
                  height={300}
                  className="!p-0 !shadow-none !bg-transparent"
                  xAxisInterval={0}
                  xAxisTickAngle={0}
                  xAxisHeight={40}
                  yAxisLabel="Bookings"
                />
                <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-850 bg-gradient-to-r from-slate-50/85 to-blue-50/30 dark:from-slate-900/50 dark:to-blue-950/30 text-sm text-gray-750 dark:text-gray-200 leading-relaxed shadow-sm transition-all duration-200">
                  <div>
                    <h4 className="font-bold text-base text-gray-900 dark:text-white tracking-tight">Period Comparison Insights</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Comparing the selected period of <strong className="text-gray-900 dark:text-white">{usageDaysCount} days</strong> against the preceding matching period (total volume: <strong className="text-gray-900 dark:text-white">{ambulanceCount + doctorCount}</strong> vs <strong className="text-gray-900 dark:text-white">{ambulancePrev + doctorPrev}</strong> bookings).
                    </p>
                  </div>

                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-left text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800 text-xs font-bold text-gray-450 dark:text-gray-500 uppercase tracking-wider">
                          <th className="pb-2 font-semibold">Service Metric</th>
                          <th className="pb-2 text-right font-semibold">Current Count</th>
                          <th className="pb-2 text-right font-semibold">Previous Count</th>
                          <th className="pb-2 text-right font-semibold">Trend Change</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-150 dark:divide-gray-800">
                        {/* Render Ambulance Row if selected or all */}
                        {(selectedServiceFilter === 'all' || selectedServiceFilter === 'Ambulance Bookings') && (
                          <tr className="hover:bg-gray-50/30 dark:hover:bg-gray-800/20 transition-colors">
                            <td className="py-2.5 flex items-center gap-2 font-medium text-gray-800 dark:text-gray-200">
                              <span className="h-2 w-2 rounded-full bg-blue-500" />
                              Ambulance Bookings
                            </td>
                            <td className="py-2.5 text-right font-semibold text-gray-900 dark:text-white font-mono">{ambulanceCount}</td>
                            <td className="py-2.5 text-right text-gray-550 dark:text-gray-400 font-mono">{ambulancePrev}</td>
                            <td className={`py-2.5 text-right font-bold font-mono ${ambPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                              {ambPct >= 0 ? '▲ +' : '▼ '}{ambPct}%
                            </td>
                          </tr>
                        )}

                        {/* Render Doctor Row if selected or all */}
                        {(selectedServiceFilter === 'all' || selectedServiceFilter === 'Doctor Consultations') && (
                          <tr className="hover:bg-gray-50/30 dark:hover:bg-gray-800/20 transition-colors">
                            <td className="py-2.5 flex items-center gap-2 font-medium text-gray-800 dark:text-gray-200">
                              <span className="h-2 w-2 rounded-full bg-emerald-500" />
                              Doctor Consultations
                            </td>
                            <td className="py-2.5 text-right font-semibold text-gray-900 dark:text-white font-mono">{doctorCount}</td>
                            <td className="py-2.5 text-right text-gray-550 dark:text-gray-400 font-mono">{doctorPrev}</td>
                            <td className={`py-2.5 text-right font-bold font-mono ${docPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                              {docPct >= 0 ? '▲ +' : '▼ '}{docPct}%
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          }

          if (serviceUsageViewMode === 'trend') {
            return (
              <Chart
                data={filteredServiceUsageTrend}
                xKey="date"
                yKeys={trendYKeys}
                colors={trendColors}
                type="area"
                height={300}
                className="!p-0 !shadow-none !bg-transparent"
                xAxisInterval="preserveStartEnd"
                xAxisTickAngle={-12}
                xAxisHeight={45}
                yAxisLabel="Bookings"
              />
            );
          }

          return (
            <div className="relative h-[300px] w-full flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart onMouseLeave={() => setActiveServiceIndex(null)}>
                  <Pie
                    data={filteredServiceUsageData.map((s) => ({
                      name: s.service,
                      value: s.count,
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={90}
                    outerRadius={115}
                    paddingAngle={3}
                    dataKey="value"
                    activeIndex={activeServiceIndex !== null ? activeServiceIndex : undefined}
                    activeShape={renderServiceActiveShape}
                    onMouseEnter={(_, index) => setActiveServiceIndex(index)}
                  >
                    {filteredServiceUsageData.map((entry, index) => {
                      const originalIndex = serviceUsageData.findIndex((s) => s.service === entry.service);
                      const colors = ['#3b82f6', '#10b981'];
                      return (
                        <Cell key={`cell-${index}`} fill={colors[originalIndex !== -1 ? originalIndex : index]} />
                      );
                    })}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Visual donut center display */}
              <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none select-none max-w-[170px] px-4">
                {activeServiceIndex !== null && filteredServiceUsageData[activeServiceIndex] ? (
                  <>
                    <span className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500 truncate w-full">
                      {filteredServiceUsageData[activeServiceIndex].service}
                    </span>
                    <span className="text-2xl font-black text-slate-900 dark:text-white mt-0.5 font-mono">
                      {filteredServiceUsageData[activeServiceIndex].count}
                    </span>
                    <span className="text-[11px] font-bold text-blue-555 dark:text-blue-400 mt-0.5">
                      {ambulanceCount + doctorCount > 0
                        ? `${((filteredServiceUsageData[activeServiceIndex].count / (ambulanceCount + doctorCount)) * 100).toFixed(1)}%`
                        : '0.0%'
                      }
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Total Bookings
                    </span>
                    <span className="text-3xl font-black text-slate-900 dark:text-white mt-0.5">
                      {selectedServiceFilter === 'all'
                        ? (ambulanceCount + doctorCount)
                        : (selectedServiceFilter === 'Ambulance Bookings' ? ambulanceCount : doctorCount)}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* 5. Full-Width Horizontal Executive Hub: Ecosystem Feature Adoption */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 mb-6">
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-gray-100 dark:border-gray-700/60 pb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium dark:text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-500" />
              Ecosystem Feature Adoption
            </h3>
            <MetricInfoPopover title="Ecosystem Feature Adoption" info={userDashboardMetricInfo.ecosystemFeatureAdoption} />
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
              EXECUTIVE MODULE TRENDS
            </span>

            {/* Time Horizon Selector */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700/60 p-0.5 rounded-md text-xs">
              {(['7d', '30d', '90d'] as const).map((th) => (
                <button
                  key={th}
                  onClick={() => setEcosystemTimeHorizon(th)}
                  className={`px-2 py-0.5 rounded-sm font-semibold transition-all ${
                    ecosystemTimeHorizon === th
                      ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {th.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Executive Feature Sub-banner */}
        <div className="mb-3.5 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/60 dark:bg-indigo-950/40 text-xs">
          <div className="flex items-center gap-1.5 font-bold text-xs text-indigo-900 dark:text-indigo-200">
            <span className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
            Executive Control Hub:
          </div>
          <p className="mt-1 text-indigo-800 dark:text-indigo-300 text-[11px] leading-relaxed">
            Click any module card below to focus its daily trend line graph. Click an already selected card to return to the overall ecosystem view. Use the <strong className="text-indigo-950 dark:text-indigo-100 font-bold">'Open Full Analytics ↗'</strong> button above the graph to open detailed analytics!
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
          {/* 1. Health Blogs & Content */}
          <div
            onClick={() => setSelectedEcosystemModule((prev) => (prev === 'blogs' ? 'all' : 'blogs'))}
            className={`group relative border rounded-lg p-3 transition-all cursor-pointer flex flex-col justify-between ${
              selectedEcosystemModule === 'blogs'
                ? 'border-blue-500 bg-blue-50/40 dark:bg-blue-950/40 ring-2 ring-blue-400/50 shadow-md'
                : selectedEcosystemModule === 'all'
                ? 'border-blue-300 dark:border-blue-700 bg-blue-50/20 dark:bg-blue-950/20 hover:border-blue-400'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/60 opacity-50 hover:opacity-100'
            }`}
          >
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-blue-500" />
                  Health Blogs
                </p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                  Content Hub
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <p className="text-xl font-bold text-gray-900 dark:text-white font-mono">
                  28 Readers
                </p>
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  ▲ +{getDynamicGrowthPct('blogs', ecosystemTimeHorizon)}% engagement
                </p>
              </div>
            </div>

            {/* Sparkline Graph */}
            <div className="h-9 w-full mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getEcosystemTrendData('blogs', ecosystemTimeHorizon).map((d) => ({ v: d.count }))}>
                  <defs>
                    <linearGradient id="blogGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke="#3b82f6" strokeWidth={2} fill="url(#blogGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Hover Tooltip */}
            <div className="absolute left-1/2 -top-14 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-40 w-60 whitespace-normal text-center leading-tight bg-gray-900 dark:bg-gray-950 text-white dark:text-gray-100 text-[11px] font-medium p-2.5 rounded-lg shadow-2xl border border-gray-700">
              Shows patient readership growth over the selected {ecosystemTimeHorizon.toUpperCase()} horizon. Click to focus trend line graph below!
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-950" />
            </div>
          </div>

          {/* 2. Digital Health Storage */}
          <div
            onClick={() => setSelectedEcosystemModule((prev) => (prev === 'storage' ? 'all' : 'storage'))}
            className={`group relative border rounded-lg p-3 transition-all cursor-pointer flex flex-col justify-between ${
              selectedEcosystemModule === 'storage'
                ? 'border-cyan-500 bg-cyan-50/40 dark:bg-cyan-950/40 ring-2 ring-cyan-400/50 shadow-md'
                : selectedEcosystemModule === 'all'
                ? 'border-cyan-300 dark:border-cyan-700 bg-cyan-50/20 dark:bg-cyan-950/20 hover:border-cyan-400'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/60 opacity-50 hover:opacity-100'
            }`}
          >
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                  <Database className="w-3.5 h-3.5 text-cyan-500" />
                  Health Storage
                </p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300">
                  Digital Locker
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <p className="text-xl font-bold text-gray-900 dark:text-white font-mono">
                  47.3 MB
                </p>
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  ▲ +{getDynamicGrowthPct('storage', ecosystemTimeHorizon)}% growth
                </p>
              </div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                5 Patients saving records
              </p>
            </div>

            {/* Sparkline Graph */}
            <div className="h-9 w-full mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getEcosystemTrendData('storage', ecosystemTimeHorizon).map((d) => ({ v: d.count }))}>
                  <defs>
                    <linearGradient id="storageGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke="#06b6d4" strokeWidth={2} fill="url(#storageGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Hover Tooltip */}
            <div className="absolute left-1/2 -top-14 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-40 w-60 whitespace-normal text-center leading-tight bg-gray-900 dark:bg-gray-950 text-white dark:text-gray-100 text-[11px] font-medium p-2.5 rounded-lg shadow-2xl border border-gray-700">
              Shows digital record storage growth over the selected {ecosystemTimeHorizon.toUpperCase()} horizon. Click to focus trend line graph below!
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-950" />
            </div>
          </div>

          {/* 3. Vedika AI Assistant */}
          <div
            onClick={() => setSelectedEcosystemModule((prev) => (prev === 'ai' ? 'all' : 'ai'))}
            className={`group relative border rounded-lg p-3 transition-all cursor-pointer flex flex-col justify-between ${
              selectedEcosystemModule === 'ai'
                ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/40 ring-2 ring-emerald-400/50 shadow-md'
                : selectedEcosystemModule === 'all'
                ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/20 dark:bg-emerald-950/20 hover:border-emerald-400'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/60 opacity-50 hover:opacity-100'
            }`}
          >
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                  <Bot className="w-3.5 h-3.5 text-emerald-500" />
                  Vedika AI
                </p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                  AI Chatbot
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <p className="text-xl font-bold text-gray-900 dark:text-white font-mono">
                  21 Users
                </p>
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  ▲ +{getDynamicGrowthPct('ai', ecosystemTimeHorizon)}% users
                </p>
              </div>
            </div>

            {/* Sparkline Graph */}
            <div className="h-9 w-full mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getEcosystemTrendData('ai', ecosystemTimeHorizon).map((d) => ({ v: d.count }))}>
                  <defs>
                    <linearGradient id="aiGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke="#10b981" strokeWidth={2} fill="url(#aiGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Hover Tooltip */}
            <div className="absolute left-1/2 -top-14 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-40 w-60 whitespace-normal text-center leading-tight bg-gray-900 dark:bg-gray-950 text-white dark:text-gray-100 text-[11px] font-medium p-2.5 rounded-lg shadow-2xl border border-gray-700">
              Shows AI chatbot consultation user growth over the selected {ecosystemTimeHorizon.toUpperCase()} horizon. Click to focus trend line graph below!
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-950" />
            </div>
          </div>

          {/* 4. Her Phases */}
          <div
            onClick={() => setSelectedEcosystemModule((prev) => (prev === 'herPhases' ? 'all' : 'herPhases'))}
            className={`group relative border rounded-lg p-3 transition-all cursor-pointer flex flex-col justify-between ${
              selectedEcosystemModule === 'herPhases'
                ? 'border-pink-500 bg-pink-50/40 dark:bg-pink-950/40 ring-2 ring-pink-400/50 shadow-md'
                : selectedEcosystemModule === 'all'
                ? 'border-pink-300 dark:border-pink-700 bg-pink-50/20 dark:bg-pink-950/20 hover:border-pink-400'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/60 opacity-50 hover:opacity-100'
            }`}
          >
            <div>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-pink-500" />
                  Her Phases
                </p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-300">
                  Women's Health
                </span>
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <p className="text-xl font-bold text-gray-900 dark:text-white font-mono">
                  12 Patients
                </p>
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  ▲ +{getDynamicGrowthPct('herPhases', ecosystemTimeHorizon)}% loggers
                </p>
              </div>
            </div>

            {/* Sparkline Graph */}
            <div className="h-9 w-full mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={getEcosystemTrendData('herPhases', ecosystemTimeHorizon).map((d) => ({ v: d.count }))}>
                  <defs>
                    <linearGradient id="herGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ec4899" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#ec4899" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke="#ec4899" strokeWidth={2} fill="url(#herGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Hover Tooltip */}
            <div className="absolute left-1/2 -top-14 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-40 w-60 whitespace-normal text-center leading-tight bg-gray-900 dark:bg-gray-950 text-white dark:text-gray-100 text-[11px] font-medium p-2.5 rounded-lg shadow-2xl border border-gray-700">
              Shows female patient wellness tracking growth over the selected {ecosystemTimeHorizon.toUpperCase()} horizon. Click to focus trend line graph below!
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-950" />
            </div>
          </div>
        </div>

        {/* Dynamic Expanded Interactive Feature Trend Line Chart */}
        <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700/60">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <div>
              <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-500" />
                {selectedEcosystemModule === 'blogs' && '📖 Health Blogs Daily Readership Trend'}
                {selectedEcosystemModule === 'storage' && '💾 Health Storage Usage & Record Upload Trend'}
                {selectedEcosystemModule === 'ai' && '🤖 Vedika AI Assistant Consultations Trend'}
                {selectedEcosystemModule === 'herPhases' && '🌸 Her Phases Wellness & Period Logging Trend'}
                {selectedEcosystemModule === 'all' && '📊 Overall Ecosystem Feature Engagement Trend'}
              </h4>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Daily timeline chart showing patient interaction volume over the past {ecosystemTimeHorizon.toUpperCase()} horizon.
              </p>
            </div>

            <button
              onClick={() => {
                if (selectedEcosystemModule === 'blogs') navigate('/insights/analytics-dashboard?tab=engagement');
                else if (selectedEcosystemModule === 'storage') navigate('/health-record-usage');
                else if (selectedEcosystemModule === 'ai') navigate('/chatbot-intelligence');
                else if (selectedEcosystemModule === 'herPhases') navigate('/users');
                else navigate('/insights/analytics-dashboard');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-300 text-xs font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-all border border-indigo-200/60 dark:border-indigo-800/60 self-start sm:self-auto"
            >
              Open Full {selectedEcosystemModule === 'blogs' ? 'Blogs' : selectedEcosystemModule === 'storage' ? 'Storage' : selectedEcosystemModule === 'ai' ? 'AI' : selectedEcosystemModule === 'herPhases' ? 'Women\'s Health' : 'Ecosystem'} Analytics
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="h-52 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={getEcosystemTrendData(selectedEcosystemModule, ecosystemTimeHorizon)}>
                <defs>
                  <linearGradient id="mainEcosystemGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={getModuleColor(selectedEcosystemModule)} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={getModuleColor(selectedEcosystemModule)} stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" opacity={0.15} />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '0.5rem',
                    color: '#f8fafc',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                  formatter={(value: any) => [`${value} ${getModuleUnit(selectedEcosystemModule)}`, 'Activity Volume']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={getModuleColor(selectedEcosystemModule)}
                  strokeWidth={3}
                  fill="url(#mainEcosystemGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 6. Side-by-Side Square Grid: Top User Locations & User Retention Intelligence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top User Locations (Square Layout) */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium dark:text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-rose-500" />
                  Top User Locations
                </h3>
                <MetricInfoPopover title="Top User Locations" info={userDashboardMetricInfo.topUserLocations} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300">
                Geographic Pulse
              </span>
            </div>

            {/* Location Insight Sub-banner */}
            <div className="mb-3.5 p-3 rounded-lg border border-rose-200 dark:border-rose-800/60 bg-rose-50/60 dark:bg-rose-950/40 text-xs">
              <div className="flex items-center gap-1.5 font-bold text-xs text-rose-900 dark:text-rose-200">
                <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                Geographic Overview:
              </div>
              <p className="mt-1 text-rose-800 dark:text-rose-300 text-[11px] leading-relaxed">
                Distribution of active patients and healthcare bookings across top cities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div>
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-1">
                  <Users className="w-4 h-4 text-primary-500" />
                  Top Cities by Users
                </p>
                <div className="space-y-2">
                  {topLocationsUsers.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">No data available.</p>
                  ) : (
                    topLocationsUsers.map((c, idx) => (
                      <div key={c.city} className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800/50 pb-1">
                        <span className="text-xs text-gray-700 dark:text-gray-200">
                          {idx + 1}. {c.city}
                        </span>
                        <span className="text-xs font-bold text-gray-900 dark:text-gray-100 font-mono">
                          {c.users.toLocaleString()} users
                        </span>
                      </div>
                    )))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-1">
                  <Globe2 className="w-4 h-4 text-emerald-500" />
                  Top Cities by Orders
                </p>
                <div className="space-y-2">
                  {topLocationsOrders.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">No data available.</p>
                  ) : (
                    topLocationsOrders.map((c, idx) => (
                      <div key={c.city} className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800/50 pb-1">
                        <span className="text-xs text-gray-700 dark:text-gray-200">
                          {idx + 1}. {c.city}
                        </span>
                        <span className="text-xs font-bold text-gray-900 dark:text-gray-100 font-mono">
                          {c.orders.toLocaleString()} orders
                        </span>
                      </div>
                    )))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow dark:shadow-gray-900/30 flex flex-col justify-between">
          <div>
            <div className="mb-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium dark:text-white flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-violet-500" />
                  User Retention Intelligence
                </h3>
                <MetricInfoPopover title="User Retention Intelligence" info={userDashboardMetricInfo.userRetention} />
              </div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
                Healthcare Benchmarks
              </span>
            </div>

            {/* Executive Retention Insights Sub-banner */}
            <div className="mb-3.5 p-3 rounded-lg border border-violet-200 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/40 text-xs">
              <div className="flex items-center gap-1.5 font-bold text-xs text-violet-900 dark:text-violet-200">
                <span className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
                Executive Insight:
              </div>
              <p className="mt-1 text-violet-800 dark:text-violet-300 text-[11px] leading-relaxed">
                Tracks patient re-engagement. Healthcare industry benchmark is <strong className="text-violet-950 dark:text-violet-100 font-bold">20% 30-Day Retention</strong>.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3.5 mt-2">
              {retentionMetrics.map((m) => {
                let benchmarkText = '';
                let benchmarkColor = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
                let subHint = '';
                let hoverExplanation = '';

                if (m.label.includes('1-Day')) {
                  benchmarkText = 'Target: 25% (Day 1)';
                  subHint = 'Next-day patient return rate';
                  hoverExplanation = 'Measures % of patients active yesterday who returned to open Vedika today.';
                  if (m.value >= 25) benchmarkColor = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300';
                  else if (m.value > 0) benchmarkColor = 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300';
                } else if (m.label.includes('7-Day')) {
                  benchmarkText = 'Target: 35% (Day 7)';
                  subHint = 'Weekly active return rate';
                  hoverExplanation = 'Measures % of patients active 7 days ago who returned to use Vedika this week.';
                  if (m.value >= 35) benchmarkColor = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300';
                  else if (m.value > 0) benchmarkColor = 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300';
                } else if (m.label.includes('30-Day')) {
                  benchmarkText = 'Target: 20% (Day 30)';
                  subHint = 'Monthly active retention';
                  hoverExplanation = 'Measures % of patients active 30 days ago who remain active today (low churn).';
                  if (m.value >= 20) benchmarkColor = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300';
                  else if (m.value > 0) benchmarkColor = 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300';
                } else {
                  benchmarkText = 'Repeat Ratio';
                  subHint = 'Repeat vs first-time signups';
                  hoverExplanation = 'Measures repeat active patients visiting today vs brand-new signups today.';
                  benchmarkColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
                }

                return (
                  <div
                    key={m.label}
                    className="group relative border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-900/60 hover:border-violet-400 dark:hover:border-violet-500 transition-all cursor-help"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{m.label}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${benchmarkColor}`}>
                        {benchmarkText}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white mt-1 font-mono">
                      {m.value}%
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 font-medium truncate">
                      {subHint}
                    </p>

                    {/* Interactive Custom Hover Tooltip (Wrapped & Centered) */}
                    <div className="absolute left-1/2 -top-12 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-40 w-56 whitespace-normal text-center leading-tight bg-gray-900 dark:bg-gray-950 text-white dark:text-gray-100 text-[11px] font-medium p-2 rounded-lg shadow-2xl border border-gray-700">
                      {hoverExplanation}
                      <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-950" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
